import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import DeleteConfirmationModal from "components/Modal/DeleteConfirmationModal";
import { showToast } from "components/Toaster";
import {
  faUser,
  faMapMarkerAlt,
  faLeaf,
  faSeedling,
  faCalendarAlt,
  faRulerCombined,
  faShoppingCart,
  faFileAlt,
  faDownload,
  faTrash,
  faCamera,
  faVideo,
  faImage,
  faReceipt
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { epochToDate, epochToGMTFormatted } from "utility/dateFormat";
import httpClient from "services/network/httpClient";

const isVideo = (url: string) => {
  return /\.(mp4|webm|ogg)(\?|$)/i.test(url);
};

interface FarmerDetailsProps {
  id: number;
  farmerName: string;
  village: string;
  district: string;
  pinCode: string;
  state: string;
  latitude: string;
  longitude: string;
  createdDate: number;
  productNames: Array<{ id: number; name: string }>;
  cropName: string;
  cultivatedAcres: string;
  fromSeedingPeriod: string;
  toSeedingPeriod: string;
  fromHarvestingPeriod: string;
  toHarvestingPeriod: string;
  url?: string; // Main demo image
  videoUrl?: string; // Video demonstration
  invoiceUrl?: string; // Invoice image
  notes: string;
  purchaseQuantity: number;
  nextFollowUpDate: number;
  // Removed images array as we'll handle media differently
  demoLocation?: string; // New field for demo location
  isPending?: boolean
}

interface ViewDetailsProps {
  viewDetails: FarmerDetailsProps;
  onDeleteSuccess: () => void;
  onClose: () => void
}


// Sample images for testing - replace with actual data in production
const defaultImages: string[] = [
  "https://placeholderimage.co/600x400/d1d5db/374151?text=No%20Image%20Available",
  "https://placeholderimage.co/600x400/d1d5db/374151?text=No%20Video%20Available",
  "https://placeholderimage.co/600x400/d1d5db/374151?text=No%20InvoiceImage%20Available"
];

// Media types for our gallery
type MediaType = 'image' | 'video' | 'invoice';

interface MediaItem {
  url: string;
  type: MediaType;
  label: string;
}

const getFieldType = (type: MediaType) => {
  switch (type) {
    case "image":
      return "IMAGE";
    case "video":
      return "VIDEO";
    case "invoice":
      return "INVOICE";
    default:
      return "IMAGE";
  }
};
function capitalizeFirstLetter(str: string) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
interface MediaGalleryProps {
  mediaItems: MediaItem[];
  productDemoId: number;
  onDeleteSuccess: () => void; // add this
  onClose: () => void
}
// Media Gallery Component
const MediaGallery: React.FC<MediaGalleryProps> = ({ mediaItems, productDemoId, onDeleteSuccess, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<MediaItem | null>(null);

  //remove delete & download icon after deleting
  const PLACEHOLDER_KEYWORDS = [
    "No%20Image%20Available",
    "No%20Video%20Available",
    "No%20InvoiceImage%20Available",
  ];

  const isDeletedMedia = (url?: string) => {
    if (!url) return true;

    const isS3Deleted =
      url.includes("amazonaws.com") && url.includes("_image");

    const isPlaceholder =
      url.includes("placeholderimage.co") &&
      PLACEHOLDER_KEYWORDS.some(keyword => url.includes(keyword));

    return isS3Deleted || isPlaceholder;
  };
  const handleDownload = () => {
    if (!selectedItem?.url) return;

    // Extract filename from URL
    const filename =
      selectedItem.url.split("/").pop()?.split("?")[0] || "download";

    try {
      const link = document.createElement("a");
      link.href = selectedItem.url;
      link.download = filename; // force download
      document.body.appendChild(link);

      // trigger click
      const clickEvent = new MouseEvent("click");
      link.dispatchEvent(clickEvent);

      document.body.removeChild(link);
    } catch (err) {
      console.error("Download failed, opening in new tab", err);

      // fallback
      window.open(selectedItem.url, "_blank", "noopener,noreferrer");
      alert(
        "Download may be blocked due to CORS. File opened in new tab, please save manually."
      );
    }
  };






  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      const response = await httpClient.delete(`/product-demo/delete-files`, {
        data: {
          productDemoId: productDemoId, // get dynamically from props
          fieldsToDelete: [getFieldType(deleteItem.type)],
        },
      });
      console.log(response);
      if (response.success === true) {
        const blobType = [capitalizeFirstLetter(getFieldType(deleteItem.type))];
        console.log(blobType);
        setIsDeleteOpen(false);
        setDeleteItem(null);
        onDeleteSuccess(); // notify parent
        showToast(`${blobType} deleted successfully`, "success");
        onClose()
      }
    } catch (error: any) {
      console.error("Delete failed", error);
      alert("Failed to delete image. Please try again.");
    }
  };

  // Get the currently selected media item
  const selectedItem = mediaItems[selectedIndex];

  // Handle media loading state
  const handleMediaLoad = () => {
    setIsLoading(false);
  };

  // Navigate to previous item with useCallback
  const prevItem = React.useCallback(() => {
    setIsLoading(true);
    setSelectedIndex(prev => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  }, [mediaItems.length]);

  // Navigate to next item with useCallback
  const nextItem = React.useCallback(() => {
    setIsLoading(true);
    setSelectedIndex(prev => (prev === mediaItems.length - 1 ? 0 : prev + 1));
  }, [mediaItems.length]);

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isFullscreen) {
        if (e.key === "ArrowLeft") prevItem();
        else if (e.key === "ArrowRight") nextItem();
        else if (e.key === "Escape") setIsFullscreen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen, prevItem, nextItem]);

  if (mediaItems.length === 0) {
    return null;
  }

  // Render thumbnail content based on media type (extracted to avoid nested ternary)
  const renderThumbnailContent = (item: MediaItem, idx: number) => {
    if (item.type === 'video') {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
          <FontAwesomeIcon icon={faVideo} className="text-white text-lg" />
        </div>
      );
    }
    if (item.type === 'invoice') {
      return (
        <>
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <FontAwesomeIcon icon={faReceipt} className="text-gray-600 text-lg" />
          </div>
          <img
            src={item.url}
            alt={`Thumbnail ${idx + 1}`}
            className="w-full h-full object-cover opacity-60"
          />
        </>
      );
    }
    return (
      <img
        src={item.url}
        alt={`Thumbnail ${idx + 1}`}
        className="w-full h-full object-cover"
      />
    );
  };

  // Render the media item based on its type
  const renderMedia = (item: MediaItem, isFullscreenView = false) => {
    if (item.type === 'video') {
      return (
        <video
          key={item.url}
          controls
          className={`${isFullscreenView ? 'max-h-[90vh] max-w-[90vw]' : 'w-full h-full'} object-contain`}
          onLoadedData={handleMediaLoad}
        >
          <source src={item.url} />
          <track kind="captions" />
        </video>
      );
    } else {
      return (
        <img
          key={item.url} // optional but good
          src={item.url}
          alt={item.label}
          className={`${isFullscreenView ? 'max-h-[90vh] max-w-[90vw]' : 'w-full h-full'} object-contain`}
          onLoad={handleMediaLoad}
        />
      );
    }
  };

  // Get the appropriate icon for the media type
  const getMediaIcon = (type: MediaType) => {
    switch (type) {
      case 'video': return faVideo;
      case 'invoice': return faReceipt;
      default: return faImage;
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 mb-4 shadow-sm"
      >
        <div className="aspect-w-16 aspect-h-9 relative">
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </motion.div>
          )}

          {/* Main media display */}
          {/* <div className="w-full max-h-full flex items-center justify-center bg-black">
            {renderMedia(selectedItem)}
          </div> */}
          <div className="relative w-full h-64 bg-black flex items-center justify-center">
            {renderMedia(selectedItem)}
          </div>


          {/* Media controls */}
          {!isDeletedMedia(selectedItem.url) && (
            <div className="absolute top-2 right-2 flex space-x-2">

              {/* Download */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleDownload}
                className="p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
              >
                <FontAwesomeIcon icon={faDownload} />
              </motion.button>

              {/* Delete */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => {
                  setDeleteItem(selectedItem);
                  setIsDeleteOpen(true);
                }}
                className="p-2 rounded-full text-white hover:bg-red-600"
              >
                <FontAwesomeIcon icon={faTrash} />
              </motion.button>

              {/* Fullscreen (existing) */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={toggleFullscreen}
                className="p-2 rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
              >
                <FontAwesomeIcon icon={faCamera} />
              </motion.button>

            </div>
          )}
          {/* Navigation arrows */}
          {mediaItems.length > 1 && (
            <>
              <motion.button
                whileHover={{ scale: 1.1, x: -2 }}
                whileTap={{ scale: 0.9 }}
                onClick={prevItem}
                className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 focus:outline-none transition-all"
                aria-label="Previous item"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1, x: 2 }}
                whileTap={{ scale: 0.9 }}
                onClick={nextItem}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-2 focus:outline-none transition-all"
                aria-label="Next item"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </motion.button>
            </>
          )}
        </div>

        {/* Thumbnail navigation */}
        {mediaItems.length > 1 && (
          <div className="flex overflow-x-auto py-2 px-1 space-x-2 bg-gray-800 dark:bg-gray-800">
            {mediaItems.map((item, index) => (
              <motion.button
                key={item.url}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setIsLoading(true);
                  setSelectedIndex(index);
                }}
                className={`flex-shrink-0 w-16 h-12 overflow-hidden rounded focus:outline-none relative ${selectedIndex === index ? 'ring-2 ring-blue-500' : ''
                  }`}
              >
                {renderThumbnailContent(item, index)}

                {/* Media type badge */}
                <div className="absolute bottom-0 right-0 bg-black bg-opacity-70 p-0.5 rounded-tl">
                  <FontAwesomeIcon
                    icon={getMediaIcon(item.type)}
                    className="text-white text-xs"
                  />
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </motion.div>

      {/* Fullscreen modal */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={toggleFullscreen}
              className="absolute top-4 right-4 p-2 rounded-full bg-white bg-opacity-25 text-white hover:bg-opacity-50 transition-all"
              aria-label="Close fullscreen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </motion.button>

            {/* Fullscreen media */}
            <div className="max-w-full max-h-full">
              {renderMedia(selectedItem, true)}
            </div>

            {/* Fullscreen navigation */}
            {mediaItems.length > 1 && (
              <>
                <motion.button
                  whileHover={{ scale: 1.1, x: -5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={prevItem}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-25 hover:bg-opacity-50 rounded-full p-3 text-white"
                  aria-label="Previous item"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1, x: 5 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={nextItem}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-25 hover:bg-opacity-50 rounded-full p-3 text-white"
                  aria-label="Next item"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </motion.button>
              </>
            )}

            {/* Item counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
              {selectedIndex + 1} / {mediaItems.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        entityName={deleteItem?.label || "file"}
        showDownloadWarning={true}
      />
    </>
  );
};

const ViewDetails: React.FC<ViewDetailsProps> = ({ viewDetails, onDeleteSuccess, onClose }) => {
  const {
    farmerName, village, district, pinCode, state,
    latitude, longitude, demoLocation, createdDate, productNames, cropName,
    cultivatedAcres, fromSeedingPeriod, toSeedingPeriod,
    fromHarvestingPeriod, toHarvestingPeriod, url, notes, videoUrl, invoiceUrl, purchaseQuantity, nextFollowUpDate, isPending
  } = viewDetails;

  // Create media items array from the URLs
  const mediaItems: MediaItem[] = [];

  // Add image if available
  if (url) {
    mediaItems.push({
      url,
      type: 'image',
      label: 'Demonstration Image'
    });
  }

  // Add video if available
  if (videoUrl) {
    mediaItems.push({
      url: videoUrl,
      type: isVideo(videoUrl) ? 'video' : 'image', // ✅ FIX
      label: 'Demonstration Media'
    });
  }

  // Add invoice if available
  if (invoiceUrl) {
    mediaItems.push({
      url: invoiceUrl,
      type: 'invoice',
      label: 'Invoice'
    });
  }

  // Use default images only if no media is available
  const displayMediaItems = mediaItems.length > 0 ? mediaItems : defaultImages.map(url => ({
    url,
    type: 'image' as MediaType,
    label: 'Sample Image'
  }));

  // Format the date using our utility
  const formattedDate = epochToDate(createdDate);

  // Group related information for better organization
  const locationData = [
    { label: "Latitude", value: latitude, icon: faMapMarkerAlt },
    { label: "Longitude", value: longitude, icon: faMapMarkerAlt },
    // { label: "Demo Location", value: demoLocation || "N/A", icon: faMapMarkerAlt }
  ];

  const farmData = [
    { label: "Crop Name", value: cropName, icon: faSeedling },
    { label: "Cultivated Acres", value: cultivatedAcres, icon: faRulerCombined },
  ];

  const periodData = [
    { label: "From Seeding Period", value: fromSeedingPeriod, icon: faCalendarAlt },
    { label: "To Seeding Period", value: toSeedingPeriod, icon: faCalendarAlt },
    { label: "From Harvesting Period", value: fromHarvestingPeriod, icon: faCalendarAlt },
    { label: "To Harvesting Period", value: toHarvestingPeriod, icon: faCalendarAlt },
  ];

  const farmerData = [
    { label: "Farmer Name", value: farmerName, icon: faUser },
    { label: "Village", value: village, icon: faMapMarkerAlt },
    { label: "District", value: district, icon: faMapMarkerAlt },
    { label: "State", value: state, icon: faMapMarkerAlt },
    { label: "Pin Code", value: pinCode, icon: faMapMarkerAlt },
  ];

  // Render a data item with consistent styling
  const renderDataItem = (item: { label: string; value: string | number, icon?: any }) => (
    <motion.div
      key={item.label}
      whileHover={{ y: -2, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
      className="flex flex-col space-y-1 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600"
    >
      <div className="flex items-center">
        {item.icon && (
          <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-2">
            <FontAwesomeIcon icon={item.icon} className="text-xs text-blue-600 dark:text-blue-400" />
          </div>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400">{item.label}</span>
      </div>
      <span className="font-medium text-gray-800 dark:text-gray-200">{item.value || "-"}</span>
    </motion.div>
  );

  // Render a section with title and data items
  const renderSection = (title: string, data: { label: string; value: string | number, icon?: any }[], sectionIcon?: any, isFullWidth = false) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`${isFullWidth ? 'col-span-full' : ''} mb-5`}
    >
      <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
        {sectionIcon && (
          <div className="h-7 w-7 rounded-full bg-blue-500/10 flex items-center justify-center mr-2">
            <FontAwesomeIcon icon={sectionIcon} className="text-blue-600 dark:text-blue-400" />
          </div>
        )}
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {data.map(renderDataItem)}
      </div>
    </motion.div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="px-5 bg-white dark:bg-gray-800 rounded-xl shadow-sm"
    >
      <motion.div
        initial={{ y: -10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="mb-5 flex flex-col md:flex-row md:justify-between md:items-center gap-3"
      >
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200 flex items-center">
            <span className="mr-2">Demo Details</span>
            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 px-4 py-2 rounded-md p-">
              Created on {formattedDate}
            </span>
          </h2>
          {purchaseQuantity > 0 && (
            <div className="mt-1 flex items-center">
              <FontAwesomeIcon icon={faShoppingCart} className="mr-2 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Purchased <span className="font-medium text-amber-700 dark:text-amber-400">{purchaseQuantity}</span> units
              </span>
            </div>
          )}
        </div>


        <motion.div className="flex flex-col  gap-3 sm:gap-4 bg-gray-50 dark:bg-gray-700/50 px-4 py-3 rounded-lg">
          {
            isPending === false && (<div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-emerald-600 dark:text-emerald-400" />
              <span className="font-medium"> Follow Up:</span>
              <span className="ml-1">{nextFollowUpDate ? epochToGMTFormatted(nextFollowUpDate) : "N/A"}</span>
            </div>)
          }
          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-blue-600 dark:text-blue-400" />
            <span className="font-medium">Demo Location:</span>
            <span className="ml-1">{demoLocation || "N/A"}</span>
          </div>
        </motion.div>

      </motion.div>

      {/* Media Gallery Section */}
      {displayMediaItems.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="mb-6"
        >
          <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <div className="h-7 w-7 rounded-full bg-blue-500/10 flex items-center justify-center mr-2">
              <FontAwesomeIcon icon={faCamera} className="text-blue-600 dark:text-blue-400" />
            </div>
            Demonstration Media
          </h3>
          <MediaGallery
            mediaItems={displayMediaItems}
            productDemoId={viewDetails.id}
            onDeleteSuccess={onDeleteSuccess} // pass the actual parent callback
            onClose={onClose}
          />

        </motion.div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conditionally show farmer details if they exist */}
        {farmerData.some(item => item.value) && renderSection("Farmer Information", farmerData, faUser)}

        {renderSection("Location Details", locationData, faMapMarkerAlt)}
        {renderSection("Farm Information", farmData, faSeedling)}

        {/* Products Section */}
        {productNames && productNames.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-5"
          >
            <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <div className="h-7 w-7 rounded-full bg-emerald-500/10 flex items-center justify-center mr-2">
                <FontAwesomeIcon icon={faLeaf} className="text-emerald-600 dark:text-emerald-400" />
              </div>
              Products ({productNames.length})
            </h3>
            <div className="grid grid-cols-1 gap-2">
              {productNames.map((product, index) => (
                <motion.div
                  key={product.id || index}
                  whileHover={{ y: -2, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                  className="flex items-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800"
                >
                  <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center mr-3">
                    <FontAwesomeIcon icon={faLeaf} className="text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="font-medium text-gray-800 dark:text-gray-200">{product.name}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {renderSection("Growing Period", periodData, faCalendarAlt)}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="col-span-full"
        >
          <h3 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
            <div className="h-7 w-7 rounded-full bg-blue-500/10 flex items-center justify-center mr-2">
              <FontAwesomeIcon icon={faFileAlt} className="text-blue-600 dark:text-blue-400" />
            </div>
            Additional Information
          </h3>

          <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
            <span className="text-sm text-gray-500 dark:text-gray-400">Notes</span>
            <div className="mt-2 text-gray-700 dark:text-gray-300 p-3 bg-white dark:bg-gray-800 rounded-lg min-h-[80px] whitespace-pre-wrap">
              {notes || "No additional notes available."}
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};
export default ViewDetails;